import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Camera, ExternalLink, Search, Undo2 } from 'lucide-react';
import {
  DEALER_STOCK_OPTIONS,
  HOME_VALUES,
  NO_CURRENT_VALUE,
  VENDOR_STOCK_OPTIONS,
  fetchHomeValueChecksQueue,
  fetchHomeValueVendors,
  undoHomeValueCheck,
} from '../api/home_value_checks';
import type {
  DealerStockFilter,
  HomeValueCheck,
  HomeValueQueueFilters,
  HomeValueQueueOrigin,
  VendorOption,
  VendorStockFilter,
} from '../api/home_value_checks';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FilterSelect } from '../components/ui/filter-select';
import { MultiSelect } from '../components/ui/multi-select';
import { DataTable } from '../components/data-table/DataTable';
import {
  ACTIONS_COL_WIDTH_COMPACT,
  ExpandedFields,
  ExpandedPanel,
  ExpandedSection,
  ScreenshotLink,
  expandColumn,
} from '../components/data-table/columns';
import {
  HomeValueActionBadge,
  LocationChips,
  dealerColumn,
  nsIdColumn,
  queuedAtColumn,
  reasonColumn,
  reviewerColumn,
  skuColumn,
  transitionColumn,
  vendorColumn,
  vendorTotalColumn,
} from '../components/data-table/homeValueColumns';
import {
  RowActionButton,
  RowActionMenu,
  RowActions,
  type RowMenuItem,
} from '../components/data-table/RowActions';
import {
  ColumnsMenu,
  FilterBar,
  FilterField,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtDateTime, fmtVendor } from '../utils/format';
import { restoreRows, takeRows } from '../utils/optimistic';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

const ORIGIN_OPTIONS = [
  { value: '',       label: 'All' },
  { value: 'manual', label: 'Manual approval',  dot: 'bg-sky-500' },
  { value: 'auto',   label: 'Auto-approved',    dot: 'bg-green-500' },
];

const HOME_VALUE_OPTIONS = [
  { value: '', label: 'All' },
  ...HOME_VALUES.map(v => ({ value: v, label: v })),
];

const CURRENT_VALUE_OPTIONS = [
  ...HOME_VALUE_OPTIONS,
  { value: NO_CURRENT_VALUE, label: 'No value set' },
];

/**
 * Home Value → Queue.
 *
 * Decided proposals waiting for the NetSuite push — approved by an operator in
 * Review, or auto-approved by the two-level gate (global switch + the vendor's
 * toggle). The push job drains this list through the job runner; a row leaves
 * for Processed the moment its push lands. The same contract as Price Checks →
 * Queue, including the one action: a manual approval can be returned to Review
 * until it is pushed, an auto-approved row cannot — the brakes for the auto lane
 * are the two toggles and the push pause.
 */
export default function HomeValueQueuePage() {
  const { user } = useAuth();
  const canReview = user?.role === 'reviewer' || user?.role === 'admin';

  const [filters, setFilters] = useState<HomeValueQueueFilters>({ page: 1, per_page: 50 });
  const [vendorCodes, setVendorCodes]     = useState<string[]>([]);
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [search, setSearch] = useState('');

  const [items, setItems]     = useState<HomeValueCheck[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [busy, setBusy]       = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  // The undo closure lives in a `columns` useMemo keyed on [canReview, busy];
  // reading the rows through a ref keeps the optimistic snapshot honest.
  const itemsRef = useRef<HomeValueCheck[]>([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const data = await fetchHomeValueChecksQueue({
        ...filters,
        vendor_codes: vendorCodes.length ? vendorCodes : undefined,
        q: search.trim() || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filters, vendorCodes, search]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchHomeValueVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
  }, []);

  async function handleUndo(id: number) {
    const { remaining, snapshot } = takeRows(itemsRef.current, [id]);
    setActionError(null);
    setBusy(true);
    setItems(remaining);
    setTotal(t => t - 1);
    try {
      await undoHomeValueCheck(id);
    } catch (e) {
      setItems(prev => restoreRows(prev, snapshot));
      setTotal(t => t + 1);
      const msg = e instanceof Error ? e.message : 'request failed';
      setActionError(`Return to Review failed — the row was restored. ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  function resetFilters() {
    setVendorCodes([]);
    setSearch('');
    setFilters({ page: 1, per_page: filters.per_page });
  }

  const hasActiveFilters =
    vendorCodes.length > 0 ||
    !!search ||
    !!filters.origin ||
    !!filters.proposed_home_value ||
    !!filters.current_home_value ||
    !!filters.vendor_stock || !!filters.dealer_stock ||
    !!filters.queued_from || !!filters.queued_to;

  const columns = useMemo<ColumnDef<HomeValueCheck>[]>(() => [
    expandColumn<HomeValueCheck>(),
    skuColumn,
    vendorColumn,
    nsIdColumn,
    transitionColumn,
    reasonColumn,
    vendorTotalColumn,
    dealerColumn,
    {
      id: 'action',
      meta: { label: 'Action' },
      header: 'Action',
      cell: ({ row }) => <HomeValueActionBadge reviewStatus={row.original.review_status} />,
    },
    reviewerColumn,
    queuedAtColumn,
    {
      id: 'actions',
      size: ACTIONS_COL_WIDTH_COMPACT,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const menuItems: RowMenuItem[] = [
          {
            label: 'Details',
            icon: Search,
            href: `/all-items/${item.ns_item_id}?from=home-value-queue&checkId=${item.id}`,
          },
          {
            label: 'Open in NetSuite',
            icon: ExternalLink,
            href: `${NS_BASE_URL}${item.ns_item_id}`,
          },
        ];
        if (item.magento_id != null) {
          menuItems.push({
            label: 'Open in Magento',
            icon: ExternalLink,
            href: `${MAGENTO_BASE_URL}${item.magento_id}`,
          });
        }
        if (item.screenshot_url) {
          menuItems.push({
            label: 'View screenshot',
            icon: Camera,
            href: item.screenshot_url,
            separatorBefore: true,
          });
        }
        return (
          <RowActions>
            {/* Auto-approved rows are not undoable — the two toggles and the
                push pause are the brakes for the auto lane (as in Price Checks). */}
            {canReview && item.review_status === 'approved' && (
              <RowActionButton
                label="Return to Review"
                icon={Undo2}
                disabled={busy}
                onClick={() => void handleUndo(item.id)}
              />
            )}
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    },
    // handleUndo reads the rows through itemsRef; the memo only re-runs when the
    // buttons themselves change (role gate, in-flight state).
  ], [canReview, busy]);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.id),
    state: { expanded, columnVisibility },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: { columnPinning: { left: ['expand', 'sku'], right: ['actions'] } },
  });

  const perPage    = filters.per_page ?? 50;
  const page       = filters.page ?? 1;
  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  return (
    <>
      <PageHeading title="Home Value — Queue" count={total} />

      <FilterBar>
        <FilterRow>
          <MultiSelect
            label="Vendor"
            options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
            selected={vendorCodes}
            onChange={next => { setVendorCodes(next); setFilters(f => ({ ...f, page: 1 })); }}
          />
          <FilterSelect
            label="Origin"
            options={ORIGIN_OPTIONS}
            value={filters.origin ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, origin: (v || undefined) as HomeValueQueueOrigin | undefined, page: 1 }))
            }
          />
          <FilterSelect
            label="From"
            options={CURRENT_VALUE_OPTIONS}
            value={filters.current_home_value ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, current_home_value: v || undefined, page: 1 }))
            }
          />
          <FilterSelect
            label="To"
            options={HOME_VALUE_OPTIONS}
            value={filters.proposed_home_value ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, proposed_home_value: v || undefined, page: 1 }))
            }
          />
          <FilterSelect
            label="Vendor stock"
            options={VENDOR_STOCK_OPTIONS}
            value={filters.vendor_stock ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, vendor_stock: (v || undefined) as VendorStockFilter | undefined, page: 1 }))
            }
          />
          <FilterSelect
            label="Dealer stock"
            options={DEALER_STOCK_OPTIONS}
            value={filters.dealer_stock ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, dealer_stock: (v || undefined) as DealerStockFilter | undefined, page: 1 }))
            }
          />
        </FilterRow>

        <FilterRow className="gap-4">
          <FilterField label="Search">
            <Input
              placeholder="SKU or name"
              className="w-[200px]"
              value={search}
              onChange={e => { setSearch(e.target.value); setFilters(f => ({ ...f, page: 1 })); }}
            />
          </FilterField>

          <FilterField label="Queued">
            <Input
              type="date"
              className="w-[140px]"
              value={filters.queued_from ?? ''}
              onChange={e =>
                setFilters(f => ({ ...f, queued_from: e.target.value || undefined, page: 1 }))
              }
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="date"
              className="w-[140px]"
              value={filters.queued_to ?? ''}
              onChange={e =>
                setFilters(f => ({ ...f, queued_to: e.target.value || undefined, page: 1 }))
              }
            />
          </FilterField>

          <div className="ml-auto flex items-center gap-2">
            {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      {actionError && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          <span>{actionError}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionError(null)} className="text-red-600">
            Dismiss
          </Button>
        </div>
      )}

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="Queue is empty — nothing is waiting for the NetSuite push"
        renderExpanded={row => {
          const item = row.original;
          return (
            <ExpandedPanel>
              <ExpandedSection title="Item">
                <ExpandedFields
                  rows={[
                    ['Description', item.item_name ?? '—'],
                    ['Manufacturer', item.manufacturer ?? '—'],
                    ['Vendor part no.', <span className="font-mono">{item.sku ?? '—'}</span>],
                    ['NetSuite Home Value now', item.ns_home_value_now ?? '—'],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Vendor stock at decision time">
                <LocationChips locations={item.locations} vendorCode={item.vendor_code} />
                <ExpandedFields
                  rows={[
                    ['PDC total', item.pdc_total ?? '—'],
                    ['Dealer stock', item.dealer_total ?? '—'],
                    ['Observed', fmtDateTime(item.availability_observed_at)],
                    ['Queued', fmtDateTime(item.reviewed_at ?? item.created_at)],
                    ['Push error', item.ns_push_error ?? '—'],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Vendor screenshot">
                <ScreenshotLink url={item.screenshot_url} />
              </ExpandedSection>
            </ExpandedPanel>
          );
        }}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={p => setFilters(f => ({ ...f, page: p }))}
        onPerPageChange={n => setFilters(f => ({ ...f, per_page: n, page: 1 }))}
      />
    </>
  );
}
